import Big from 'big.js';
import imagesLoaded from 'imagesloaded';
import Masonry from 'masonry-layout';
import { GetChildPrice, GetOptionPrice, GetPrice, GetPrices, GetSizes, GetTypes, PriceSheetMinQuantity, SizeNameMatches, TypeNameMatches } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import * as html from 'smg/html';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { Debounce, DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { CustomQtyBtnInit } from '../components/buttons';
import { RadioBorderListener } from '../components/forms/form-control';
import { GetMoldFee } from '../moldfees';
import { RequiredChildFloor } from '../pricing';
import { SwiperInit } from '../swiper';

let currProduct: string;
let currType: string;
let currSubType: string;
let currMinQty = 0;
let lineItemTypeSize: models.LineItemTypeSize | undefined;
let swiperIndex: number = 0;
let currentProductChanged: boolean = false;

let $addlRowCostItems: HTMLElement[] = [];
let galleryMasonry: Masonry | null = null;

const debounceSpinner = Debounce(() => {
    const $priceEstimateSpinner = document.querySelector<HTMLElement>('#price-estimate-spinner');
    $priceEstimateSpinner?.classList.remove('d-none');
}, 500, false);

main();

function main() {
    const $pricingContainer = document.querySelector('#pricing-products-types-container') as HTMLElement;

    // Init border listener
    RadioBorderListener();

    // Scoped Funcs
    const dispatchChangeEventIfChecked = ($productTypeRadios: HTMLInputElement[]) => {
        for (let i = 0; i < $productTypeRadios.length; i++) {
            if ($productTypeRadios[i].checked) {
                $productTypeRadios[i].dispatchEvent(new Event('change'));
                swiperIndex = i;
            }
        }
    };

    const hasListeners: Element[] = [];
    const checkAddListeners = ($productTypeRadios: HTMLInputElement[], $productTab: HTMLElement) => {
        if (!hasListeners.includes($productTab)) {
            addRadioListeners($productTypeRadios);
            hasListeners.push($productTab);
        }
    };

    const createSizeElementsForProduct = ($productTypeRadios: HTMLInputElement[]) => {
        const type = currTypeInput($productTypeRadios);
        const product = type?.dataset.product;
        const allowedSizes = type?.dataset.sizes ? new Set(type.dataset.sizes.split(';')) : undefined;
        if (type && product) {
            void createSizeEls(currProduct, type.value || currType, currSubType, '', type.dataset.href ?? '', allowedSizes).then(() => RadioBorderListener());
        }
    };

    // Page initialization
    const $productTypeRadios = getRadios();
    dispatchChangeEventIfChecked($productTypeRadios);
    const $productTabs = Array.from($pricingContainer.querySelectorAll<HTMLElement>('.nav-link.product-tab'));
    const activeTab = $productTabs.find($productTab => $productTab.classList.contains('active'));
    if (activeTab) {
        currType = currTypeInput($productTypeRadios)?.value || '';
        currProduct = currTypeInput($productTypeRadios)?.dataset.product || '';
        currSubType = currTypeInput($productTypeRadios)?.dataset.subtype || '';
        currMinQty = parseInt(currTypeInput($productTypeRadios)?.dataset.minQty ?? '', 10) || 0;
        checkAddListeners($productTypeRadios, activeTab);
        createSizeElementsForProduct($productTypeRadios);
        aboutSection(currProduct, currType, currSubType);
        updatePricingChart(activeTab.id.replace('-tab', '').replaceAll('-', ' '), currTypeInput($productTypeRadios)?.value || '');
        gallerySection(currProduct, currType, currSubType);
    }

    for (const $productTab of $productTabs) {
        $productTab.addEventListener('shown.bs.tab', () => {
            currProduct = $productTab.id.replace('-tab', '');
            currentProductChanged = true;
            const $productTypeRadios = getRadios();
            checkAddListeners($productTypeRadios, $productTab);
            dispatchChangeEventIfChecked($productTypeRadios);
        });
    }

    const $pricingCalcTabPaneBtn = document.querySelector<HTMLElement>('#calculator-tab');
    const $pricingChartTabPaneBtn = document.querySelector<HTMLElement>('#chart-tab');
    const $pricingCalcTabPane = document.querySelector<HTMLElement>('#pricing-calculator-tab-pane');
    const $pricingChartTabPane = document.querySelector<HTMLElement>('#pricing-chart-tab-pane');

    if (QueryString().get('chart')) {
        $pricingCalcTabPaneBtn?.classList.remove('active', 'show');
        $pricingChartTabPaneBtn?.classList.add('active', 'show');
        $pricingCalcTabPane?.classList.remove('active', 'show');
        $pricingChartTabPane?.classList.add('active', 'show');
    }

    $pricingChartTabPaneBtn?.addEventListener('shown.bs.tab', () => {
        QueryString().set('chart', 'true');
    });
    $pricingCalcTabPaneBtn?.addEventListener('shown.bs.tab', () => {
        QueryString().delete('chart');
    });

    // const masonry = new Masonry('#short-gallery', {
    //     itemSelector: '.gallery-item',
    //     gutter: 24,
    // });

    // masonry.layout?.();
    SwiperInit({
        slideTo: {
            index: swiperIndex,
            speed: 500,
        },
    });
}

// Helpers

function getRadios(): HTMLInputElement[] {
    const $currTabPane = document.querySelector('.tab-pane.product-tab-pane.active') as HTMLElement;
    const $productTypeRadios = Array.from($currTabPane.querySelectorAll<HTMLInputElement>('.product-type-input'));
    return $productTypeRadios;
}

function addRadioListeners($productTypeRadios: HTMLInputElement[]) {
    for (const $productTypeRadio of $productTypeRadios) {
        $productTypeRadio.addEventListener('change', () => {
            currType = $productTypeRadio.value;
            const subType = $productTypeRadio.dataset.subtype ?? '';
            currSubType = subType;
            currMinQty = parseInt($productTypeRadio.dataset.minQty ?? '', 10) || 0;

            if ($productTypeRadio.dataset.pricingpage) {
                window.history.replaceState({}, '', $productTypeRadio.dataset.pricingpage);
            }
            const $productType = $productTypeRadio.value;
            const $product = $productTypeRadio.dataset.product;
            if ($product) {
                const $typeSizesContainer = document.querySelector('#type-sizes-container') as HTMLElement;
                const preSelectedSize = $typeSizesContainer.querySelector<HTMLInputElement>('input:checked')?.value;
                const type = currTypeInput($productTypeRadios);
                const sizes = type?.dataset.sizes ? new Set(type.dataset.sizes.split(';')) : undefined;
                void createSizeEls($product, $productType, subType ?? '', preSelectedSize ?? '', $productTypeRadio.dataset.href ?? '', sizes).then(() => RadioBorderListener());
            }
            if ($product && $productType) {
                updatePricingChart($product, $productType);
                aboutSection($product, $productType, subType);
                gallerySection($product, $productType, subType);
                scrollToPricingSection();
            }
        });
    }
}

function currTypeInput($typeRadios: HTMLInputElement[]): HTMLInputElement | undefined {
    return $typeRadios.find($typeRadio => $typeRadio.checked);
}

async function createSizeEls(lineItem: string, type: string, subType: string, preSelectedSize: string, typePage: string, allowedSizes?: Set<string>) {
    const baseImgHeight = 68;
    const baseFontSize = 1.125;

    const $sizesContainer = document.querySelector('#type-sizes-container') as HTMLElement;
    await GetSizes(lineItem, type).then(resp => {
        $sizesContainer.innerHTML = '';
        const sizeRadios = [];
        for (const size of resp) {
            if (!(size.networkLineItemTypeSize?.prices ?? []).length) {
                continue;
            }

            if (allowedSizes?.size) {
                let allowed = false;
                for (const label of allowedSizes) {
                    if (SizeNameMatches(size, label)) {
                        allowed = true;
                        break;
                    }
                }
                if (!allowed) {
                    continue;
                }
            }
            if (size.networkLineItemTypeSize?.enabled) {
                const uuid = UUID();
                const $radioEl = template.Clone(
                    `.product-size-radio-template[data-product='${CSS.escape(lineItem)}']` +
                    `[data-type='${CSS.escape(subType || type)}']`,
                ).firstElementChild as HTMLLabelElement;
                const $productTypeImg = $radioEl.querySelector('.product-type-img') as HTMLImageElement;
                if (lineItem === 'Lapel Pins' && size.type && size.type !== 'Circle') {
                    const $img = template.Clone(`#${html.Anchorize(lineItem)}-${html.Anchorize(size.type)}`);
                    if ($img) {
                        $productTypeImg.closest('picture')?.replaceWith($img);
                    }
                }
                const $inputEl = $radioEl.querySelector('input') as HTMLInputElement;
                const $sizeText = $radioEl.querySelector('h5') as HTMLElement;
                const sizeLabel = size.networkName() || size.name;
                const $quarterImg = $radioEl.querySelector<HTMLImageElement>('.quarter-img');
                const sizeWidth = size.width.toNumber();
                const sizeHeight = size.height.toNumber();
                const scaleAvg = ((1 / sizeHeight) + (1 / sizeWidth)) / 2;
                if (lineItem.toLowerCase() === 'lanyards') {
                    $productTypeImg.height = $productTypeImg.height / scaleAvg;
                    if ($productTypeImg.width > 0) {
                        $productTypeImg.width = $productTypeImg.width / scaleAvg;
                    }
                } else {
                    if (sizeHeight !== sizeWidth) {
                        if (sizeHeight > 1) {
                            $productTypeImg.height = $productTypeImg.height / (1 / sizeHeight);
                        } else {
                            $productTypeImg.height = $productTypeImg.height * (1 / sizeHeight);
                        }
                        if ($productTypeImg.width > 0) {
                            if (sizeWidth > 1) {
                                $productTypeImg.width = $productTypeImg.width / (1 / sizeWidth);
                            } else {
                                $productTypeImg.width = $productTypeImg.width * (1 / sizeWidth);
                            }
                        }
                    } else {
                        const quarterSize = $quarterImg ? $quarterImg.height : baseImgHeight;
                        if ($productTypeImg.width > quarterSize) {
                            const widthScale = quarterSize / $productTypeImg.width;
                            $productTypeImg.height = $productTypeImg.height * widthScale;
                            $productTypeImg.width = $productTypeImg.width * widthScale;
                        }
                        if (scaleAvg > 1) {
                            $productTypeImg.height = $productTypeImg.height / scaleAvg;
                            if ($productTypeImg.width > 0) {
                                $productTypeImg.width = $productTypeImg.width / scaleAvg;
                            }
                        } else if ($quarterImg) {
                            $quarterImg.height = $quarterImg.height * scaleAvg;
                            if ($quarterImg.width > 0) {
                                $quarterImg.width = $quarterImg.width * scaleAvg;
                            }
                        }
                    }
                }
                $radioEl.htmlFor = uuid;
                $inputEl.id = uuid;
                $inputEl.name = `${html.Anchorize(type)}-size-radio`;
                $inputEl.value = sizeLabel;
                $inputEl.dataset.width = size.width.toString();
                $inputEl.dataset.height = size.height.toString();
                $inputEl.dataset.sizeID = size.id;
                $sizeText.textContent = sizeLabel;
                $sizesContainer.appendChild($radioEl);
                sizeRadios.push($inputEl);
                const $imgContainer = $radioEl.querySelector('.img-container') as HTMLElement;
                if ($imgContainer && $imgContainer.offsetWidth > 127) {
                    const $scale = 128 / $imgContainer.offsetWidth;
                    $productTypeImg.height = $productTypeImg.height * $scale;
                    if ($productTypeImg.width > 0) {
                        $productTypeImg.width = $productTypeImg.width * $scale;
                    }
                    if ($quarterImg) {
                        $quarterImg.height = $quarterImg.height * $scale;
                        if ($quarterImg.width > 0) {
                            $quarterImg.width = $quarterImg.width * $scale;
                        }
                    }
                }
                adjustFontSize(sizeLabel.length, $sizeText, $imgContainer.offsetHeight || baseImgHeight);
            }
        }
        RadioBorderListener();
        for (const sizeRadio of sizeRadios) {
            sizeRadio.addEventListener('change', async () => {
                const size = resp.find(s => s.id === sizeRadio.dataset.sizeID);
                if (!size) {
                    return;
                }
                lineItemTypeSize = size;
                QueryString().set('size', sizeRadio.value);
                const preSelectedQty = document.querySelector<HTMLInputElement>('.qty-input:checked')?.value;
                await createQtyEls(
                    lineItem,
                    type,
                    size.networkName() || size.name,
                    size.width.toNumber(),
                    size.height.toNumber(),
                    parseInt(preSelectedQty || '0', 10) || 0,
                    typePage,
                );
            });
        }
        if (QueryString().get('size')) {
            const $radio = sizeRadios.find($radio => $radio.value === QueryString().get('size'));
            if ($radio) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
            } else {
                sizeRadios[0].checked = true;
                sizeRadios[0].dispatchEvent(new Event('change'));
            }
        } else if (preSelectedSize) {
            for (const sizeRadio of sizeRadios) {
                if (sizeRadio.value === preSelectedSize) {
                    sizeRadio.checked = true;
                    sizeRadio.dispatchEvent(new Event('change'));
                    break;
                }
            }
            if (sizeRadios.length && !sizeRadios.some(sizeRadio => sizeRadio.checked)) {
                sizeRadios[0].checked = true;
                sizeRadios[0].dispatchEvent(new Event('change'));
            }
        } else {
            if (sizeRadios.length) {
                sizeRadios[0].checked = true;
                sizeRadios[0].dispatchEvent(new Event('change'));
            }
        }
    });

    function adjustFontSize(charLength: number, $textEl: HTMLElement, imgElHeight: number) {
        if (charLength < 18 && imgElHeight <= baseImgHeight) {
            return;
        } else {
            const newFontSize = baseFontSize - ((charLength - 17) + ((imgElHeight - baseImgHeight) / 3)) * 0.05;
            $textEl.style.fontSize = `${newFontSize}rem`;
        }
    }
}

let customQtyHasListeners: boolean = false;
async function createQtyEls(lineItem: string, type: string, sizeName: string, width: number, height: number, preSelectedQty: number, typePage: string) {
    customQtyHasListeners = false;
    const minQty = await PriceSheetMinQuantity(lineItem, currMinQty);
    await GetPrices(lineItem, type, width, height, undefined, sizeName).then(async resp => {
        const displayPrices = resp.filter(price => price.quantity >= minQty);
        const $qtyRadioContainer = document.querySelector('#qty-radio-container') as HTMLElement;
        $qtyRadioContainer.innerHTML = '';
        const qtyPrices = [];
        for (const qtyPrice of displayPrices) {
            qtyPrices.push(qtyPrice.price.toNumber());
        }
        const basePrice = Math.max(...qtyPrices);
        const lowestQty = Math.min(...displayPrices.map(qtyPrice => qtyPrice.quantity));
        for (const qtyPrice of displayPrices) {
            const $qtyRadioContainerFirstChild = template.Clone('#qty-radio-template').firstElementChild as HTMLLabelElement;
            const $qtyRadio = $qtyRadioContainerFirstChild.querySelector('input') as HTMLInputElement;
            $qtyRadio.value = qtyPrice.quantity.toString();
            const addlUnitCost = await getAddlUnitCosts(lineItem, type);
            if (addlUnitCost.unitCost.gt(0)) {
                $qtyRadio.dataset.unitPrice = qtyPrice.price.add(addlUnitCost.unitCost).toFixed(2);
            } else {
                $qtyRadio.dataset.unitPrice = qtyPrice.price.toFixed(2);
            }
            const $qtySpan = $qtyRadioContainerFirstChild.querySelector('.quantity') as HTMLElement;
            const $priceSpan = $qtyRadioContainerFirstChild.querySelector('.price-per-unit') as HTMLElement;
            const $saveSpan = $qtyRadioContainerFirstChild.querySelector('.save') as HTMLElement;
            const percentageSavings = ((basePrice - qtyPrice.price.toNumber()) / basePrice) * 100;
            if (percentageSavings > 0) {
                $saveSpan.textContent = `SAVE ${Math.round(percentageSavings)}% `;
            } else {
                $saveSpan.textContent = '';
                const $qtyRadioTextDivider = $qtyRadioContainerFirstChild.querySelector('.qty-radio-text-divider') as HTMLElement;
                $qtyRadioTextDivider.style.visibility = 'hidden';
            }
            $qtySpan.textContent = qtyPrice.quantity.toLocaleString();
            let lineItemStr: string | undefined;
            switch (lineItem) {
            case 'lapel pins':
                lineItemStr = 'pin';
                break;
            case 'patches':
                lineItemStr = 'patch';
                break;
            case 'lanyards':
                lineItemStr = 'lanyard';
                break;
            case 'key chains':
            case 'keychains':
                lineItemStr = 'keychain';
                break;
            case 'challenge coins':
                lineItemStr = 'coin';
                break;
            case 'belt buckles':
                lineItemStr = 'buckle';
                break;
            case 'medals':
                lineItemStr = 'medal';
                break;
            default:
                lineItemStr = 'unit';
                break;
            }
            if (addlUnitCost.unitCost.gt(0)) {
                $priceSpan.textContent = `${DollarFormat(qtyPrice.price.add(addlUnitCost.unitCost).toFixed(2))} per ${lineItemStr ? lineItemStr : lineItem} `;
            } else {
                $priceSpan.textContent = `${DollarFormat(qtyPrice.price.toFixed(2))} per ${lineItemStr ? lineItemStr : lineItem} `;
            }
            $qtyRadioContainer.appendChild($qtyRadioContainerFirstChild);
        }

        const [$customQtyRadio, $customQtyInputContainer] = createCustomRadio();
        const $qtyRadio = $customQtyRadio.querySelector('input') as HTMLInputElement;
        $qtyRadio.addEventListener('change', () => {
            if ($qtyRadio.checked) {
                $customQtyInputContainer?.classList.remove('d-none');
                const $input = $customQtyInputContainer?.querySelector<HTMLInputElement>('input');
                if ($input && QueryString().get('qty')) {
                    $input.value = QueryString().get('qty') || '100';
                }

                $qtyRadio.value = $input?.value || '1';
                $customQtyRadio.parentElement?.appendChild($customQtyInputContainer);
                if (!customQtyHasListeners) {
                    ['change', 'input'].forEach(event => $input?.addEventListener(event, () => {
                        if ($input.value) {
                            QueryString().set('qty', $input.value);
                            $qtyRadio.value = $input.value;
                            if (parseInt($input.value, 10) < minQty) {
                                void updatePriceEstimate($input.value, '0.00', lineItem, type, sizeName, typePage);
                                return;
                            }
                            void GetPrice(lineItem, type, sizeName || `${width.toString()} "`, parseInt($input.value, 10)).then(resp => {
                                void updatePriceEstimate($input.value, resp.toFixed(2), lineItem, type, sizeName, typePage);
                            });
                        }
                    }));
                    $input?.dispatchEvent(new Event('change'));
                    customQtyHasListeners = true;
                }
            }
        });
        $qtyRadioContainer.appendChild($customQtyRadio);

        const $qtyRadios = Array.from($qtyRadioContainer.querySelectorAll('input'));
        for (const $qtyRadio of $qtyRadios) {
            $qtyRadio.addEventListener('change', () => {
                if (!($qtyRadio.id === 'custom-qty-input')) {
                    $customQtyInputContainer?.classList.add('d-none');
                    void updatePriceEstimate($qtyRadio.value, $qtyRadio.dataset.unitPrice || '0', lineItem, type, sizeName, typePage);
                }
                QueryString().set('qty', $qtyRadio.value.toString());
            });
        }
        if (QueryString().get('qty')) {
            const num = parseInt(QueryString().get('qty') || '', 10);
            if (num < lowestQty) {
                QueryString().set('qty', lowestQty.toString());
            }
            const $radio = $qtyRadios.find($radio => $radio.value.toString() === QueryString().get('qty'));
            if ($radio) {
                $radio.checked = true;
                $radio.dispatchEvent(new Event('change'));
            } else if (num > 0) {
                const $customQtyInput = $qtyRadios.find($radio => $radio.id === 'custom-qty-input');
                if ($customQtyInput && $customQtyInputContainer) {
                    $customQtyInput.checked = true;
                    $customQtyInputContainer.classList.remove('d-none');
                    const $qtyInput = $customQtyInputContainer.querySelector<HTMLInputElement>('input');
                    if ($qtyInput) {
                        $qtyInput.value = num.toString();
                    }
                    $customQtyInput.dispatchEvent(new Event('change'));
                }
            } else {
                $qtyRadios[0].checked = true;
                $qtyRadios[0].dispatchEvent(new Event('change'));
            }
        } else if (preSelectedQty) {
            for (const $qtyRadio of $qtyRadios) {
                if (parseInt($qtyRadio.value, 10) === preSelectedQty) {
                    $qtyRadio.checked = true;
                    $qtyRadio.dispatchEvent(new Event('change'));
                    void updatePriceEstimate($qtyRadio.value, $qtyRadio.dataset.unitPrice || '0', lineItem, type, sizeName, typePage);
                    break;
                } else {
                    void updatePriceEstimate($qtyRadios[0].value, $qtyRadios[0].dataset.unitPrice || '0', lineItem, type, sizeName, typePage);
                }
            }
            if (!$qtyRadios.some($qtyRadio => $qtyRadio.checked)) {
                if ($qtyRadios.length > 1) {
                    $qtyRadios[0].checked = true;
                }
            }
        } else {
            $qtyRadios[0].checked = true;
            $qtyRadios[0].dispatchEvent(new Event('change'));
        }
    });
}

function createCustomRadio(): [HTMLElement, HTMLElement] {
    const $customQtyInputContainer = template.Clone('#custom-qty-input-container').firstElementChild as HTMLElement;
    const $customQtyRadio = template.Clone('#qty-radio-template').firstElementChild as HTMLElement;
    const $qtyRadio = $customQtyRadio.querySelector('input') as HTMLInputElement;
    $qtyRadio.id = 'custom-qty-input';
    $qtyRadio.dataset.custom = 'true';
    const $qtySpan = $customQtyRadio.querySelector('.quantity') as HTMLElement;
    const $priceSpan = $customQtyRadio.querySelector('.price-per-unit') as HTMLElement;
    $priceSpan.textContent = '';
    const $qtyRadioTextDivider = $customQtyRadio.querySelector('.qty-radio-text-divider') as HTMLElement;
    $qtyRadioTextDivider.style.visibility = 'hidden';
    $qtySpan.textContent = 'Custom';
    CustomQtyBtnInit($customQtyInputContainer);
    return [$customQtyRadio, $customQtyInputContainer];
}

async function updatePriceEstimate(qty: string, unitPrice: string, lineItem: string, type: string, size: string, typePage: string) {
    const $priceEstimateContainer = document.querySelector<HTMLElement>('#price-estimate-container');
    const $priceEstimateInner = $priceEstimateContainer?.querySelector<HTMLElement>('.price-estimate-inner');
    const $lowQtyMsg = $priceEstimateContainer?.querySelector<HTMLElement>('.low-qty');
    const $qty = $priceEstimateInner?.querySelector<HTMLElement>('#qty-amount');
    const $unitPrice = $priceEstimateInner?.querySelector<HTMLElement>('#unit-price-amount');
    const $unitPriceText = $priceEstimateInner?.querySelector<HTMLElement>('#unit-price-text');
    const $estimatedTotal = $priceEstimateInner?.querySelector<HTMLElement>('#estimated-total');
    const $shopAdditionalBtns = document.querySelectorAll<HTMLAnchorElement>('.shop-additional-btn');
    const $priceEstimateSpinner = $priceEstimateContainer?.querySelector<HTMLElement>('#price-estimate-spinner');

    debounceSpinner();

    const $tempItems = $addlRowCostItems;

    $addlRowCostItems = [];

    let addlSetupCosts = Big(0);
    let addlUnitCost = Big(0);
    let addlUnitCostItems: string[] = [];

    await (async () => {
        const t = (await GetTypes(lineItem ?? currProduct)).find(t => TypeNameMatches(t, type));
        const moldFee = await GetMoldFee(
            lineItem === 'Keychains' ? 'Key Chains' : lineItem,
            t?.id || '',
            lineItemTypeSize?.id || '',
            lineItemTypeSize?.width.toNumber() || Big(size).toNumber(),
            lineItemTypeSize?.height.toNumber() || Big(size).toNumber(),
            Big(qty).toNumber(),
        );

        if (moldFee.gt(Big(0))) {
            const moldFeeNotice = template.Clone('#price-estimate-row-template').firstElementChild as HTMLElement;
            const item = moldFeeNotice.querySelector('.row-item') as HTMLElement;
            const price = moldFeeNotice.querySelector('.row-price') as HTMLElement;
            item.textContent = 'Mold Fee';
            price.textContent = DollarFormat(moldFee.toFixed(2));
            addlSetupCosts = addlSetupCosts.add(moldFee);
            $addlRowCostItems.push(moldFeeNotice);
        }

        const { unitCost, items } = await getAddlUnitCosts(lineItem, type).then(resp => resp);
        addlUnitCost = unitCost;
        addlUnitCostItems = items;

        await getAddlSetupCosts(lineItem, type).then(resp => {
            addlSetupCosts = addlSetupCosts.add(resp);
        });

        const $priceEstimateRows = document.querySelector<HTMLElement>('.price-estimate-items');
        for (const $addlRowCostItem of $addlRowCostItems) {
            if ($tempItems.length) {
                $tempItems[0].remove();
            }
            $priceEstimateRows?.appendChild($addlRowCostItem);
        }
        if ($tempItems.length) {
            for (const $tempItem of $tempItems) {
                $tempItem.remove();
            }
        }
    })().finally(() => {
        debounceSpinner.cancel();
        $priceEstimateSpinner?.classList.add('d-none');
    });

    function toggleLowQtyMsg(show: boolean) {
        if (show) {
            $priceEstimateInner?.classList.add('d-none');
            $lowQtyMsg?.classList.remove('d-none');
        } else {
            $priceEstimateInner?.classList.remove('d-none');
            $lowQtyMsg?.classList.add('d-none');
        }
    }

    if ($qty) {
        $qty.textContent = parseInt(qty, 10).toLocaleString();
    }

    if ($unitPrice && $unitPriceText) {
        if (addlUnitCost.gt(0)) {
            $unitPrice.textContent = DollarFormat(Big(unitPrice).toFixed(2));

            if (addlUnitCostItems.length) {
                const addlUnitCostItemsUnique = Array.from(new Set(addlUnitCostItems));
                let str: string;
                if (addlUnitCostItemsUnique.length <= 2) {
                    str = addlUnitCostItemsUnique.join(' & ');
                } else {
                    str = `${addlUnitCostItemsUnique.slice(0, -1).join(', ')} & ${addlUnitCostItemsUnique[addlUnitCostItemsUnique.length - 1]}`;
                }
                $unitPriceText.textContent = `Unit Price (includes ${str} fees)`;
            }
        } else {
            $unitPriceText.textContent = 'Unit Price';
            if (unitPrice === '0.00') {
                toggleLowQtyMsg(true);
            } else {
                toggleLowQtyMsg(false);
                $unitPrice.textContent = DollarFormat(unitPrice);
            }
        }
    }

    if ($estimatedTotal) {
        if (parseFloat(unitPrice)) {
            if (!((parseFloat(qty) * parseFloat(unitPrice)) > 0)) {
                toggleLowQtyMsg(true);
            } else {
                toggleLowQtyMsg(false);
                $estimatedTotal.textContent = DollarFormat(Big(new Big(qty).times(new Big(unitPrice))).add(addlSetupCosts).toFixed(2));
            }
        } else {
            toggleLowQtyMsg(true);
        }
    }

    const capitalizedType = type.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    const capitalizedLineItem = lineItem.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    const capitalizedSubType = currSubType.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

    $shopAdditionalBtns.forEach($shopAdditionalBtn => {
        $shopAdditionalBtn.textContent = `Shop ${capitalizedSubType ? capitalizedSubType : capitalizedType} ${capitalizedLineItem}`;
        const url = new URL(typePage, window.location.origin);
        url.searchParams.set('size', size);
        url.searchParams.set('qty', qty);
        $shopAdditionalBtn.href = url.pathname + url.search;
    });
}

function updatePricingChart(lineItem: string, type: string) {
    const $pricingChartContainer = document.querySelector<HTMLElement>('#pricing-table-container');
    const $pricingChartTabPane = document.querySelector<HTMLElement>('#pricing-chart-tab-pane');
    const $pricingChartLineItem = $pricingChartTabPane?.querySelector<HTMLElement>('#pricing-chart-lineitem');
    if ($pricingChartLineItem) {
        $pricingChartLineItem.textContent = lineItem;
    }
    if ($pricingChartContainer) {
        void createPriceChart(lineItem, type, $pricingChartContainer);
    }
}

async function createPriceChart(lineItem: string, type: string, container: HTMLElement) {
    const sizes = await GetSizes(lineItem, type);
    const minQty = await PriceSheetMinQuantity(lineItem, currMinQty);

    const qtys = new Set<number>();
    for (const size of sizes) {
        if (!(size.networkLineItemTypeSize?.prices ?? []).length) {
            continue;
        }

        for (const price of size.networkLineItemTypeSize?.prices ?? []) {
            if (price.quantity < minQty) {
                continue;
            }

            qtys.add(price.quantity);
        }
    }

    let leftPos: string;

    if (qtys.size > 6) {
        leftPos = '-2%';
    } else {
        leftPos = '-3%';
    }

    const $tableContainer = html.FromString(`<div class="d-flex flex-column" style="margin-left: ${leftPos.replace('-', '')}; min-width: 500px">
                                    <div
                                        id="table-title"
                                        class="text-center py-4 align-self-end"
                                        style="width: 88%; margin-right: 5%; background: #D3DBF3;
                                            border-top: 2px solid #B3C1EA; border-left: 2px solid #B3C1EA;
                                            border-right: 2px solid #B3C1EA;
                                            border-top-left-radius: 1.5rem; border-top-right-radius: 1.5rem;">
                                        <span class="fw-bold">Quantity</span>
                                    </div>
                                    <div class="d-flex">
                                        <table
                                            id="size-table"
                                            class="table table-striped pricing-table text-center overflow-hidden d-inline-block w-auto"
                                            style="box-shadow: 0px 4px 6px -4px rgba(61, 69, 88, 0.12),
                                                0px 8px 8px -4px rgba(61, 69, 88, 0.08);
                                                margin-right: ${leftPos}; z-index: 1;">
                                        <thead>
                                            <tr>
                                                <th scope="col" class="text-nowrap" style="background: #D3DBF3;">Size <span class="fw-normal fst-italic">(in)</span></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        </tbody>
                                    </table>
                                        <table
                                            id="qty-table"
                                            class="table table-striped pricing-table text-center overflow-hidden"
                                            style="box-shadow: 0px 2px 4px -2px rgba(61, 69, 88, 0.12),
                                                0px 4px 4px -2px rgba(61, 69, 88, 0.08);
                                                border-top-left-radius: 0; border-bottom-left-radius: 0;">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Size</th>
                                                    <th scope="col">50</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <th scope="row">1.5"</th>
                                                    <td>$6.03</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>`);

    const $sizeTable = $tableContainer.querySelector('#size-table') as HTMLTableElement;

    const $table = $tableContainer.querySelector('#qty-table') as HTMLTableElement;
    const $thead = $table.querySelector('thead') as HTMLTableSectionElement;
    const $tbody = $table.querySelector('tbody') as HTMLTableSectionElement;

    $thead.innerHTML = '';
    $tbody.innerHTML = '';

    const $sizeTableBody = $sizeTable.querySelector('tbody') as HTMLTableSectionElement;
    $sizeTableBody.innerHTML = '';

    const qtysSorted = Array.from(qtys).sort((a, b) => a - b);

    const pricesBySize = new Map<string, Map<number, Big>>();
    for (const size of sizes) {
        const prices = new Map<number, Big>();
        for (const price of size.networkLineItemTypeSize?.prices ?? []) {
            prices.set(price.quantity, price.price);
        }
        pricesBySize.set(size.name, prices);
    }

    const $tr = html.FromString('<tr></tr>').firstElementChild as HTMLTableRowElement;
    $thead.appendChild($tr);

    for (const qty of qtysSorted) {
        $tr.appendChild(html.FromString(`<th style="background: #E3E9F8;">${Intl.NumberFormat().format(qty)}</th>`));
    }

    for (const [size, prices] of pricesBySize) {
        if (!prices.size) {
            continue;
        }

        const $tr = html.FromString('<tr></tr>').firstElementChild as HTMLTableRowElement;
        $tbody.appendChild($tr);

        const $sizeTr = html.FromString(`<tr><th class="fw-normal ps-8 text-nowrap" scope="row">${size.replaceAll('"', '')}</th></tr>`);
        $sizeTableBody.appendChild($sizeTr);

        for (const qty of qtysSorted) {
            const price = prices.get(qty);
            $tr.appendChild(html.FromString(`<td>${price ? DollarFormat(price) : ''}</td>`));
        }
    }

    const $nonHeadTrs = Array.from($tbody.querySelectorAll('tr'));
    for (const $nonHeadTr of $nonHeadTrs) {
        $nonHeadTr.style.borderBottom = 'none';
        const $nonHeadTh = $nonHeadTr.querySelector('th');
        if ($nonHeadTh) {
            $nonHeadTh.style.borderBottom = 'none';
        }
        for (const $td of Array.from($nonHeadTr.querySelectorAll('td'))) {
            $td.style.borderBottom = 'none';
        }
    }

    container.innerHTML = '';
    container.appendChild($tableContainer);
}

function aboutSection(product: string, type: string, subType?: string) {
    const $aboutSections = Array.from(document.querySelectorAll<HTMLElement>('.about-section'));
    let $section: HTMLElement | undefined;
    if (subType) {
        $section = $aboutSections.find($section => $section.dataset.product === product && $section.dataset.type === subType);
    } else {
        $section = $aboutSections.find($section => $section.dataset.product === product && $section.dataset.type === type);
    }
    if ($section) {
        for (const $aboutSection of $aboutSections) {
            if ($aboutSection !== $section) {
                $aboutSection.classList.add('d-none');
            } else {
                $aboutSection.classList.remove('d-none');
            }
        }
    }
}

function gallerySection(product: string, type: string, subType?: string) {
    const $gallerySections = Array.from(document.querySelectorAll<HTMLElement>('.gallery-section'));

    let $section: HTMLElement | undefined;
    if (subType) {
        $section = $gallerySections.find($section => $section.dataset.product === product && $section.dataset.type === subType);
    } else {
        $section = $gallerySections.find($section => $section.dataset.product === product && $section.dataset.type === type);
    }
    if ($section) {
        const hasGalleryImgs = $section.querySelectorAll('a').length > 1;

        for (const $gallerySection of $gallerySections) {
            if ($gallerySection !== $section || !hasGalleryImgs) {
                $gallerySection.classList.add('d-none');

                const existingGallery = $gallerySection.querySelector('#gallery-images');
                if (existingGallery) {
                    existingGallery.removeAttribute('id');
                }
                $gallerySection.querySelectorAll('a[data-fancybox]').forEach(a => a.removeAttribute('data-fancybox'));
            } else {
                $gallerySection.classList.remove('d-none');
            }
        }

        const $galleryImages = $section.querySelector<HTMLElement>('.row');
        if ($galleryImages) {
            $galleryImages.id = 'gallery-images';

            $galleryImages.querySelectorAll('a').forEach(a => {
                a.setAttribute('data-fancybox', type ?? 'gallery');
            });
            if ($galleryImages.childElementCount > 3) {
                galleryMasonry = new Masonry($galleryImages, {
                    itemSelector: '.gallery-image-parent:not(.d-none)',
                });

                imagesLoaded($galleryImages).on('progress', () => {
                    galleryMasonry?.layout?.();
                });
            }
        }
    }
}

async function getAddlSetupCosts(lineItem: string, type: string): Promise<Big> {
    const $addlSetupCosts = Array.from(document.querySelectorAll<HTMLElement>('.additional-one-time-fee'));
    if ($addlSetupCosts.length) {
        const $lineItems = $addlSetupCosts.filter($addlSetupCost => $addlSetupCost.dataset.lineitem === lineItem);
        if ($lineItems.length) {
            const $lineItemTypes = $lineItems.filter($lineItem => $lineItem.dataset.lineitemtype === type);
            if ($lineItemTypes.length) {
                let totalPrice = Big(0);
                for (const $lineItemType of $lineItemTypes) {
                    let price = Big(0);
                    const child = $lineItemType.getAttribute('data-child');
                    const option = $lineItemType.getAttribute('data-option');
                    if (child && !option) {
                        price = await GetChildPrice(lineItem, child);
                    } else if (child && option) {
                        price = await GetOptionPrice(lineItem, child, option);
                    }

                    const $row = template.Clone('#price-estimate-row-template').firstElementChild as HTMLElement;
                    const item = $row.querySelector('.row-item') as HTMLElement;
                    const priceEl = $row.querySelector('.row-price') as HTMLElement;
                    item.textContent = child ? child : 'Additional Setup Fee';
                    priceEl.textContent = DollarFormat(price.toFixed(2));
                    $addlRowCostItems.push($row);
                    totalPrice = totalPrice.add(price);
                }
                return totalPrice;
            }
        }
    }
    return Big(0);
}

async function getAddlUnitCosts(lineItem: string, type: string): Promise<{ unitCost: Big; items: string[] }> {
    const $addlUnitCosts = Array.from(document.querySelectorAll<HTMLElement>('.additional-unit-costs'));
    if (!$addlUnitCosts.length) {
        return { unitCost: Big(0), items: [] };
    }

    const $lineItems = $addlUnitCosts.filter($addlUnitCost => $addlUnitCost.dataset.lineitem === lineItem);
    if (!$lineItems.length) {
        return { unitCost: Big(0), items: [] };
    }

    // data-lineitemtype is the catalog productType (e.g. Soft Enamel). Subtype
    // pages (Western/Web) share that type and filter further via data-subtype
    // vs the checked radio's data-type (subType | productType).
    const $lineItemTypes = $lineItems.filter($lineItem => $lineItem.dataset.lineitemtype === type);
    if (!$lineItemTypes.length) {
        return { unitCost: Big(0), items: [] };
    }

    const $currType = getRadios().find($r => $r.checked);

    interface matchedEntry { child: string; price: Big; specific: boolean }
    const matched: matchedEntry[] = [];

    for (const $lineItemType of $lineItemTypes) {
        const entrySubtype = $lineItemType.dataset.subtype ?? '';
        if (entrySubtype) {
            // Radio data-type is subType | productType (see products-types-nav).
            if ($currType && $currType.dataset.type !== entrySubtype) {
                continue;
            }
        }

        const child = $lineItemType.getAttribute('data-child');
        if (!child) {
            continue;
        }

        const option = $lineItemType.getAttribute('data-option');
        const cheapest = $lineItemType.dataset.cheapest === 'true';

        let price: Big;
        if (cheapest) {
            // No option cards on /pricing → RequiredChildFloor falls back to catalog min.
            price = await RequiredChildFloor(lineItem, child);
        } else if (option) {
            price = await GetOptionPrice(lineItem, child, option);
        } else {
            price = await GetChildPrice(lineItem, child);
        }

        matched.push({ child, price, specific: Boolean(entrySubtype) });
    }

    // One amortized amount per child: subtype-specific entry wins over subtype-less
    // (e.g. Western pinned Hinge & Hook beats Soft Enamel Attachments floor).
    const byChild = new Map<string, matchedEntry>();
    for (const entry of matched) {
        const key = entry.child.toLocaleLowerCase();
        const existing = byChild.get(key);
        if (!existing || (entry.specific && !existing.specific)) {
            byChild.set(key, entry);
        }
    }

    let totalPrice = Big(0);
    const children: string[] = [];
    for (const entry of byChild.values()) {
        totalPrice = totalPrice.add(entry.price);
        children.push(entry.child);
    }
    return { unitCost: totalPrice, items: children };
}
function scrollToPricingSection() {
    const $calculatorDiv = document.querySelector('#pricing-calculator-tab-pane');
    const $chartDiv = document.querySelector('#pricing-chart-tab-pane');
    if (currentProductChanged === false) {
        if ($calculatorDiv?.classList.contains('active')) {
            $calculatorDiv.scrollIntoView({ behavior: 'smooth' });
        } else if ($chartDiv?.classList.contains('active')) {
            $chartDiv.scrollIntoView({ behavior: 'smooth' });
        }
    } else {
        currentProductChanged = false;
    }
}